import React from 'react';
import {
  Facebook,
  Twitter,
  Instagram,
  Youtube,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  Shield,
  ChevronRight,
} from 'lucide-react';
import { footerLinks } from '../data/mock';
import { useSettings } from '../context/SettingsContext';

const Footer = ({ onContactClick, onPageClick }) => {
  const settings = useSettings();
  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 lg:gap-8">
          {/* Brand Column */}
          <div className="space-y-3 col-span-2 md:col-span-1">
            <div className="flex items-center gap-2">
              {settings.site_logo ? (
                <img src={settings.site_logo} alt={settings.site_name} className="w-9 h-9 rounded-xl object-contain" />
              ) : (
                <div className="w-9 h-9 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-sm">{settings.logo_text || 'M+'}</span>
                </div>
              )}
              <span className="text-lg font-bold text-white">{settings.site_name}</span>
            </div>
            <p className="text-xs text-gray-400 leading-relaxed">
              Your trusted online pharmacy for genuine medications, vitamins, and healthcare products.
            </p>
            <div className="flex gap-2">
              {[
                { icon: Facebook, href: settings.social_facebook, name: 'Facebook' },
                { icon: Twitter, href: settings.social_twitter, name: 'Twitter' },
                { icon: Instagram, href: settings.social_instagram, name: 'Instagram' },
                { icon: Youtube, href: settings.social_youtube, name: 'YouTube' },
              ].filter((s) => s.href).map((social, index) => {
                const Icon = social.icon;
                return (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 bg-gray-800 rounded-lg hover:bg-teal-600 transition-colors"
                    aria-label={social.name}
                    data-testid={`footer-social-${social.name.toLowerCase()}`}
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-3">Company</h4>
            <ul className="space-y-1.5">
              {footerLinks.company.map((link, index) => (
                <li key={index}>
                  <button onClick={() => onPageClick?.(link.label)} className="text-sm hover:text-teal-400 transition-colors">
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-3">Support</h4>
            <ul className="space-y-1.5">
              {footerLinks.support.map((link, index) => (
                <li key={index}>
                  <button onClick={() => onPageClick?.(link.label)} className="text-sm hover:text-teal-400 transition-colors">
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-3">Quick Links</h4>
            <ul className="space-y-1.5">
              {[
                { label: 'All Products', key: 'All Products' },
                { label: 'Health Blog', key: 'Blog' },
                { label: 'FAQ', key: 'FAQ' },
                { label: 'New Arrivals', key: 'New Arrivals' },
              ].map((link, index) => (
                <li key={index}>
                  <button onClick={() => onPageClick?.(link.key)} className="text-sm hover:text-teal-400 transition-colors">
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Us */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-3">Contact Us</h4>
            <ul className="space-y-2">
              <li className="flex items-center gap-2">
                <Mail className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" />
                <a href={`mailto:${settings.contact_email}`} className="text-sm hover:text-teal-400 truncate">
                  {settings.contact_email}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" />
                <a href={`tel:${settings.contact_phone}`} className="text-sm hover:text-teal-400">
                  {settings.contact_phone}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-3.5 h-3.5 text-teal-500 mt-0.5 flex-shrink-0" />
                <span className="text-xs text-gray-400">{settings.contact_address || '123 Healthcare Ave, Medical District'}</span>
              </li>
            </ul>
            <button onClick={onContactClick} className="mt-3 px-3 py-1.5 bg-teal-600 text-white text-xs font-medium rounded-lg hover:bg-teal-700 transition-colors">
              Contact Form
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Copyright */}
            <p className="text-sm text-gray-400">
              &copy; {new Date().getFullYear()} {settings.site_name}. All rights reserved.
            </p>

            {/* Payment & Trust Badges */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Shield className="w-4 h-4 text-teal-500" />
                <span>Secure Payments</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <CreditCard className="w-4 h-4 text-teal-500" />
                <span>Visa, Mastercard, PayPal</span>
              </div>
            </div>

            {/* Legal Links */}
            <div className="flex gap-4">
              {footerLinks.legal.slice(0, 2).map((link, index) => (
                <button
                  key={index}
                  onClick={() => onPageClick?.(link.label)}
                  className="text-sm text-gray-400 hover:text-teal-400 transition-colors"
                >
                  {link.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
